///
/// EarthBrowser
///
/// Created by Matt Giger on 10/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Camera pose <-> URL params. Launch deep links and share URLs carry the
/// pose in a human-readable form — ?lat=&lon=&alt=&tilt=&bearing= in
/// degrees and kilometers, the same convention the embed has always used —
/// so links can be read, hand-edited, and constructed by third parties.
/// The legacy raw form (?camera=radius,theta,phi,tilt,bearing in engine
/// units) is still parsed so existing shared links keep working, and wins
/// when both are present.
///

import { CameraParams } from "@/earthbrowser/activities/EarthActivity/EarthCameraState"
import { wgs } from "@/earthbrowser/utils/geographic"
import { Constants } from "@/constants"
import { trig } from "@stovokor/math/math"

/** The pose fields shared by CameraParams and CameraSavedState */
interface CameraPoseLike {
	radius: number
	theta: number
	phi: number
	tilt: number
	bearing: number
}

/**
 * Camera pose from launch URL params: raw ?camera= wins (legacy links,
 * automation), then the friendly ?lat/?lon form (?alt in km defaults to
 * the whole-earth altitude, ?tilt/?bearing in degrees default to 0).
 */
export function cameraPoseFromParams(params: URLSearchParams): CameraParams | undefined {
	const camera = params.get("camera")
	if (camera) {
		const parts = camera.split(",").map(parseFloat)
		if (parts.length === 5 && parts.every(isFinite)) {
			return new CameraParams(parts[0], parts[1], parts[2], parts[3], parts[4])
		}
	}

	const lat = parseFloat(params.get("lat") ?? "")
	const lon = parseFloat(params.get("lon") ?? "")
	if (isFinite(lat) && isFinite(lon)) {
		const altKm = parseFloat(params.get("alt") ?? "")
		const tiltDeg = parseFloat(params.get("tilt") ?? "")
		const bearingDeg = parseFloat(params.get("bearing") ?? "")
		return new CameraParams(
			wgs.a + (isFinite(altKm) ? altKm : Constants.cDefaultCameraAltitude),
			lon * trig.rad,
			(90 - lat) * trig.rad,
			isFinite(tiltDeg) ? tiltDeg * trig.rad : 0,
			isFinite(bearingDeg) ? bearingDeg * trig.rad : 0
		)
	}

	return undefined
}

/**
 * Write a camera pose onto a URL in the human-readable form. Precision:
 * 5 decimals on lat/lon (~1 m), 3 on altitude km (1 m), 2 on the angles;
 * tilt/bearing are omitted when effectively zero.
 */
export function setCameraPoseParams(url: URL, camera: CameraPoseLike): void {
	const lat = 90 - camera.phi * trig.deg
	const lon = ((camera.theta * trig.deg % 360) + 540) % 360 - 180
	url.searchParams.set("lat", round(lat, 5))
	url.searchParams.set("lon", round(lon, 5))
	url.searchParams.set("alt", round(camera.radius - wgs.a, 3))
	const tiltDeg = camera.tilt * trig.deg
	const bearingDeg = camera.bearing * trig.deg
	if (Math.abs(tiltDeg) >= 0.01) {
		url.searchParams.set("tilt", round(tiltDeg, 2))
	}
	if (Math.abs(bearingDeg) >= 0.01) {
		url.searchParams.set("bearing", round(bearingDeg, 2))
	}
}

/** Fixed-precision number string without trailing zeros */
function round(n: number, places: number): string {
	return Number(n.toFixed(places)).toString()
}
